import { Component, Input, Output, OnChanges, SimpleChanges, OnInit, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';

import { IMyDrpOptions, IMyDateRange, IMyDate } from 'mydaterangepicker';
import { TranslatePipe } from '@ngx-translate/core';

import { StatisticService } from './statistic.service';
import { SharedDataService } from '../shared/data.service';

@Component({
  selector: 'wm-statistic',
  template: require('./statistic.component.html'),
  providers: [TranslatePipe]
})
export class StatisticComponent implements OnChanges, OnInit {
  @Input() requests: Array<any>;
  @Output() listCountUpdate = new EventEmitter();

  statisticForm: FormGroup;

  preloader = true;
  loading = true;

  public currentInterval = 'day';

  public chartLoad: boolean;

  public chartType = 'line';
  public pieSupport = false;

  public lineChartData: Array<any> = [];
  public lineChartLabels: Array<any> = [];
  public lineChartOptions = {
    maintainAspectRatio: false,
    scales: {
      yAxes: [
        {
          id: 'right',
          display: false,
          type: 'linear',
          position: 'right',
          ticks: {
            beginAtZero: true
          }
        },
        {
          id: 'left',
          display: true,
          type: 'linear',
          position: 'left',
          ticks: {
            beginAtZero: true
          }
        }
      ]
    }
  };

  public pieChartData: Array<any> = [];
  public pieChartLabels: Array<any> = [];
  public pieChartOptions = {};

  public statisticDateOptions = {
    dateFormat: 'yyyy-mm-dd',
    openSelectorOnInputClick: true,
    selectBeginDateTxt: false,
    selectEndDateTxt: false,
    showApplyBtn: false,
    showClearBtn: false,
    showClearDateRangeBtn: false,
    disableSince: this.getNextDay()
  };

  constructor(
    private service: StatisticService,
    private route: ActivatedRoute,
    private shared: SharedDataService,
    private fb: FormBuilder,
    private translate: TranslatePipe
  ) { }

  ngOnInit() {
    this.buildForm();
  }

  buildForm() {
    this.statisticForm = this.fb.group({
      mydaterange: ['']
    });
  }

  setForm(start_date, end_date) {
    this.statisticForm.patchValue({
      mydaterange: {
        beginDate: this.setDate(start_date),
        endDate: this.setDate(end_date)
      }
    });
  }

  getToday(): IMyDate {
    let date = new Date();
    date.setDate(date.getDate() + 0);

    let i_date = <IMyDate> {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate()
    };

    return i_date;
  }

  getNextDay(): IMyDate {
    let date = new Date();
    date.setDate(date.getDate() + 1);

    let i_date = <IMyDate> {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate()
    };

    return i_date;
  }

  getBeginingMonth(): IMyDate {
    let date = new Date();
    date.setDate(1);

    let i_date = <IMyDate> {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate()
    };

    return i_date;
  }

  setDate(date_string: string): IMyDate {
    let date = new Date(date_string);

    let i_date = <IMyDate> {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate()
    };

    return i_date;
  }

  prepareDate(model) {
    let prepare = {};
    for (let field of Object.keys(model)) {
      if (field === 'mydaterange') {
        let splited = model['mydaterange'].formatted.split(' - ');

        prepare['start_date'] = splited[0].trim();
        prepare['end_date'] = splited[1].trim();
      }
      if (!!model[field] && !!model[field].jsdate) {
        prepare[field] = model[field].formatted;
      } else if (!!model[field] && !!model[field].date) {
        delete prepare[field];
      }
    }
    return prepare;
  }

  selectChartData() {
    if (this.chartType === 'line') {
      return this.lineChartData;
    } else {
      return this.pieChartData;
    }
  }

  selectChartLabels() {
    if (this.chartType === 'line') {
      return this.lineChartLabels;
    } else {
      return this.pieChartLabels;
    }
  }

  changeInterval(interval) {
    this.currentInterval = interval;
    this.getStatistic();
  }

  changeChartType(chart) {
    this.chartLoad = false;
    this.chartType = chart;

    setTimeout(() => {
      this.chartLoad = true;
    }, 100);
  }

  onSubmit(form, valid) {
    event.preventDefault();

    let parse_data = this.prepareDate(form);

    this.getStatistic(parse_data);

    return false;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!!this.requests) {
      this.getStatistic();

      this.loading = false;
    }
  }

  getStatistic(params = {}) {
    this.chartLoad = false;
    this.preloader = true;

    this.lineChartData = [];
    this.lineChartLabels = [];

    this.pieChartData = [];
    this.pieChartLabels = ['Просмотренно', 'Не просмотренно'];

    this.requests.forEach((request, j) => {
      let start_date = !!params['start_date'] ? params['start_date'] : request.start_date;
      let end_date = !!params['end_date'] ? params['end_date'] : request.end_date;

      if (!request.ids) {
        request.ids = Object.keys(request.checkids);
      }

      this.service.getStatics({
        ids: request.ids, metrics: request.metrics, filters: request.filters,
        date_range: {
          start_date: start_date,
          end_date: end_date,
          interval: this.currentInterval
        }
      }, request.statistic_type).subscribe(result => {
        let response = this.prepareResult(result, request);

        this.setForm(start_date, end_date);

        let eresponse = {};

        if (!!request['id']) {
          eresponse[request['id']] = response;
        } else {
          eresponse = response;
        }

        this.listCountUpdate.emit(eresponse);

        if (this.requests.length === j + 1) {
          setTimeout(() => {
            this.chartLoad = true;
          }, 300);
        }
      });
    });
  }

  prepareResult(result, request) {
    let response = {};
    response[request['statistic_type']] = {};

    Object.keys(result['reports']).forEach((metric, index) => {
      response[request['statistic_type']][metric] = {};

      let statistic = {};

      request['ids'].forEach(metric_id => {
        response[request['statistic_type']][metric][metric_id] = 0;

        let items = result['reports'][metric][metric_id];
        statistic['line'] = []; statistic['pie'] = [];

        if (this.lineChartLabels.length === 0) {
          for (let i = 0; i < items.length; i++) {
            let date = new Date(items[i]['date']);
            this.lineChartLabels.push(date.toISOString().substring(0, 10));
          }
        }

        for (let i = 0; i < items.length; i++) {
          response[request['statistic_type']][metric][metric_id] += items[i]['count'];

          if (!request.checkids || request.checkids[metric_id]) {
            statistic['line'].push(items[i]['count']);
          } else {
            statistic['line'].push(0);
          }

          if (!!request['total'] && !!request['total'][metric_id]) {
            this.pieSupport = true;

            if (statistic['pie'].length === 0) {
              statistic['pie'] = [items[i]['count'], request['total'][metric_id] - items[i]['count']];
            } else {
              statistic['pie'][0] += items[i]['count'];
              statistic['pie'][1] -= items[i]['count'];
            }
          }
        }

        this.prepareChart(statistic, request, metric, index);
      });
    });

    return response;
  }

  prepareChart(statistic, request, metric, index) {
    let tlabel = this.translate.transform(request.statistic_type + '.usage_type.' + metric);

    let chart_index = this.lineChartData.findIndex(element => {
      return element['metric'] === metric;
    });

    if (!request['hidden'] || !request['hidden'].includes(metric)) {
      if (chart_index === -1) {
        let metric_id = 'left';

        if (!!request['position'] && request['position'] === 'right') {
          this.lineChartOptions['scales']['yAxes'][0]['display'] = true;
          metric_id = 'right';
        }

        this.lineChartData.push({
          label: tlabel, data: statistic['line'], fill: false, tension: 0,
          metric: metric, yAxisID: metric_id
        });
      } else {
        for (let i = 0; i < statistic['line'].length; i++) {
          this.lineChartData[chart_index].data[i] += statistic['line'][i];
        }
      }

      if (statistic['pie'].length > 0 && index === 0) {
        if (!this.pieChartData[index]) {
          this.pieChartData.push({label: tlabel, data: statistic['pie']});
        } else {
          this.pieChartData[index].data[0] += statistic['pie'][0];
          this.pieChartData[index].data[1] += statistic['pie'][1];
        }
      }
    }
  }


  chartClicked(e: any): void {
    console.log(e);
  }

  chartHovered(e: any): void {
    console.log(e);
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/statistics/statistic.component.ts